import { isNullOrUndefined } from "@smart/itops-utils-basic";

import { AutoFillStatus, ResponseSubmitter, SectionItem } from "../../types";

export type VoiceSpeechRecognitionResult = {
  [key: number]: {
    transcript: string;
    isFinal: boolean;
  };
  isFinal: boolean;
  length: number;
};

export type VoiceSpeechRecognitionEvent = {
  resultIndex: number;
  results: ArrayLike<VoiceSpeechRecognitionResult> & {
    length: number;
  };
};

export type VoiceSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onresult: ((event: VoiceSpeechRecognitionEvent) => void) | null;
};

export type SpeechRecognitionConstructor = {
  new (): VoiceSpeechRecognition;
};

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

export const getSpeechRecognition = (): SpeechRecognitionConstructor | null => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const languageLocales: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  pt: "pt-PT",
  pl: "pl-PL",
  ru: "ru-RU",
  zh: "zh-CN",
  ja: "ja-JP",
  ko: "ko-KR",
  vi: "vi-VN",
  ar: "ar-SA",
};

export const getLocaleLanguage = (language?: string) =>
  language ? languageLocales[language.toLowerCase()] || "en-US" : "en-US";

const initialGreetings: Record<string, string> = {
  en: "Hello, I'd like to start filling out this form.",
  es: "Hola, me gustaría comenzar a completar este formulario.",
  fr: "Bonjour, j'aimerais commencer à remplir ce formulaire.",
  de: "Hallo, ich möchte dieses Formular ausfüllen.",
  it: "Ciao, vorrei iniziare a compilare questo modulo.",
  pt: "Olá, gostaria de começar a preencher este formulário.",
  pl: "Dzień dobry, chcę rozpocząć wypełnianie tego formularza.",
  ru: "Здравствуйте, я хотел бы начать заполнение этой формы.",
  zh: "你好，我想开始填写这个表格。",
  ja: "こんにちは、このフォームに記入を始めたいです。",
  ko: "안녕하세요, 이 양식을 작성하기 시작하고 싶습니다.",
  vi: "Xin chào, tôi muốn bắt đầu điền vào biểu mẫu này.",
  ar: "مرحبا، أود أن أبدأ في ملء هذا النموذج.",
};

export const getInitialGreeting = (language?: string) => {
  const key = (language || "en").toLowerCase();
  return initialGreetings[key] || initialGreetings.en;
};

export type VoiceConversationField = {
  uri: string;
  sectionUri: string;
  groupUri?: string | null;
  type?: string;
  options?: Array<{ value: string; label: string }> | null;
  allowCustomResponse?: boolean | null;
};

const mapLabelToValue = (
  options: Array<{ value: string; label: string }>,
  value: unknown,
): string | unknown => {
  const str = typeof value === "string" ? value : String(value ?? "");
  if (!str) return value;

  const found = options.find(
    (opt) =>
      String(opt.value).trim().toLowerCase() ===
      String(str).trim().toLowerCase(),
  );
  if (found) return found.value;
  return value;
};

type ValidateFieldValueResult =
  | { success: true; value: unknown }
  | { success: false; reason: string };

const parseDateFieldValue = (value: unknown): string | null => {
  const str =
    typeof value === "string" ? value.trim() : String(value ?? "").trim();
  if (!str) return null;

  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  const date = new Date(Date.UTC(year, month - 1, day));
  const isCalendarDate =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  if (!isCalendarDate) return null;

  return `${yearStr}-${monthStr}-${dayStr}`;
};

export const validateFieldValue = (
  field: VoiceConversationField,
  value: unknown,
): ValidateFieldValueResult => {
  if (isNullOrUndefined(value)) {
    return { success: false, reason: "missing value" };
  }

  // Tool use sends option labels only; we map label to value and validate.
  if (field.type === "choice" && field.options?.length) {
    const resolved = mapLabelToValue(field.options, value);
    const optionValues = new Set(field.options.map((o) => o.value));
    if (typeof resolved === "string" && optionValues.has(resolved)) {
      return { success: true, value: resolved };
    }
    if (field.allowCustomResponse) {
      return {
        success: true,
        value: typeof value === "string" ? value : String(value ?? ""),
      };
    }
    return { success: false, reason: "option not found" };
  }

  if (field.type === "checkbox" && field.options?.length) {
    const arr = Array.isArray(value) ? value : [value];
    const labels = arr.map((v) =>
      typeof v === "string" ? v : String(v ?? ""),
    );

    const optionValues = new Set(field.options.map((o) => o.value));
    const allowCustom = field.allowCustomResponse ?? false;
    const resolved = labels.map((label) =>
      mapLabelToValue(field.options!, label),
    );
    const values = resolved.filter(
      (v): v is string => typeof v === "string" && optionValues.has(v),
    );
    const custom = resolved.filter(
      (v) => typeof v === "string" && !optionValues.has(v),
    );
    if (custom.length > 0 && !allowCustom) {
      return { success: false, reason: "option not found" };
    }
    const result = [...values, ...custom.map((v) => String(v ?? ""))].filter(
      Boolean,
    );
    return { success: true, value: result };
  }

  if (field.type === "date") {
    const parsedDate = parseDateFieldValue(value);
    return parsedDate
      ? { success: true, value: parsedDate }
      : { success: false, reason: "invalid date format" };
  }

  return { success: true, value };
};

export type VoiceConversationWebSocketApi = {
  voiceConversationWebSocket: (opts: {
    actionRouteKey: string;
    onMessage?: (message: unknown) => void;
  }) => {
    connect: () => Promise<void>;
    disconnect: () => void;
    send: (message: any) => Promise<void>;
    setOnMessage: (
      handler: ((message: unknown) => void) | null | undefined,
    ) => void;
  };
};

export type VoiceConversationPreview = {
  formUri: string;
  responses: Record<string, { value: any }>;
};

export type VoiceConversationOptions = {
  submissionUri?: string;
  preview?: VoiceConversationPreview;
  submitResponses?: ResponseSubmitter;
  fields?: VoiceConversationField[];
  sectionRef?: React.RefObject<{
    setFieldValue?: (fieldUri: string, value: any) => void;
    hasUploadingFiles?: () => boolean;
  } | null>;
  visibleSections?: SectionItem[];
  selectedSection?: SectionItem;
  onSelectSection?: (section: SectionItem) => void;
  onSummary?: () => void;
  language?: string;
  voiceWebSocket: VoiceConversationWebSocketApi;
  autoFillStatus?: AutoFillStatus;
  silenceThresholdMs?: number;
  maxUtteranceMs?: number;
  firstWordGraceMs?: number;
  replyTimeoutMs?: number;
};

export const DEFAULT_SILENCE_THRESHOLD_MS = 1500;
export const DEFAULT_MAX_UTTERANCE_MS = 30_000;
export const DEFAULT_FIRST_WORD_GRACE_MS = 3_000;
export const DEFAULT_REPLY_TIMEOUT_MS = 20_000;
